import { isEmpty } from 'lodash';
import React, { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet, useRevalidator } from 'react-router';

import { useQueryParameters } from '@app/components/HigherOrderComponents/use-query-parameters';
import MarleyIcon from '@app/components/icons/MarleyIcon';
import Loading from '@app/components/Loading/loading-simple';
import { FAILURE, SUCCESS } from '@app/constants/general';
import { checkInitDone } from '@app/helpers/common';
import { trackAuthEvent, datadogRumActions } from '@app/helpers/datadog-rum';
import isLoadedFromMobile from '@app/helpers/platform';
import { useSafeAuth } from '@app/hooks/use-safe-auth';
import { NAMESPACE } from '@app/i18n';
import { StateType } from '@app/types/reducer-state';
import { resetAuthLoaderFlags } from '@app/utils/auth-loader-prefetch';

import { ErrorMessage } from './error-message';
import {
  useAuthenticationRedirect,
  useAuthInitialization,
  useClearMediaCache,
  useLogoutHandler,
  useWebSocketConnection,
} from './hooks';

// Minimum gap between soft "Try again" retries — long enough to swallow an
// accidental double-tap, short enough that a deliberate retry feels responsive.
const RETRY_DEBOUNCE_MS = 1500;

export const UserInit = () => {
  const { authState } = useSafeAuth();

  const { logout, authId, loadingStatus, accessTokenErrorMessage } =
    useSelector((state: StateType) => ({
      logout: state?.auth?.logout,
      authId: state?.auth?.user?._id,
      loadingStatus: checkInitDone(state),
      accessTokenErrorMessage: state.auth.accessTokenErrorMessage,
    }));

  useAuthInitialization();
  useWebSocketConnection();
  useClearMediaCache(authId);
  useLogoutHandler(logout);

  useQueryParameters();

  // Emit the "kept the session on a transient health-check failure" signal here
  // (once per transition) rather than in the reducer — keeps the reducer pure
  // and avoids double-firing, since the underlying error action is dispatched
  // by both doRequest.catch and apiCall.fail().
  useEffect(() => {
    if (loadingStatus === 'health-check-transient-error') {
      trackAuthEvent(datadogRumActions.HEALTH_CHECK_TRANSIENT, {
        reason: 'health_check_transient',
      });
    }
  }, [loadingStatus]);

  const { t } = useTranslation(NAMESPACE.APP_SHELL);
  const { revalidate } = useRevalidator();
  const loginId = authState?.idToken?.claims?.preferred_username;

  // Debounce retries so a rapid double-tap doesn't fire parallel
  // getUserByOktaUserId calls (a late transient error could overwrite a prior
  // SUCCESS). A time debounce — rather than a lock keyed on loadingStatus —
  // never permanently blocks: a deliberate later retry is always allowed, even
  // when the previous attempt failed with the same 'health-check-transient-error'.
  const lastRetryAtRef = useRef(0);

  // Soft, in-app retry for transient bootstrap failures: reset the loader gate
  // and re-run the React Router loader (which re-dispatches the health check)
  // instead of window.location.reload(). If the network is still down the
  // loader just fails again and re-renders this same screen (with "Back to
  // Login" intact) — no full-document navigation that would strand the user on
  // a browser "cannot open page" with no in-app way back.
  const handleRetry = () => {
    const now = Date.now();
    if (now - lastRetryAtRef.current < RETRY_DEBOUNCE_MS) return; // ignore rapid double-taps
    lastRetryAtRef.current = now;
    resetAuthLoaderFlags();
    revalidate();
  };

  // Error message logic
  let message;
  let canRetry = false;
  if (loadingStatus === FAILURE) {
    message = t('routes.errorLoadingPage');
  } else if (loadingStatus === 'user-lookup-failure') {
    message = t('routes.errorFetchingUser');
  } else if (loadingStatus === 'health-check-transient-error') {
    // Transient bootstrap failure (HMB-19203): show a retryable error instead
    // of forcing a logout; "Back to Login" remains the escape hatch.
    message = t('routes.sessionProblem');
    canRetry = true;
  } else if (loadingStatus === 'websockets-failure') {
    message = t('routes.websocketFailure');
  }

  if (accessTokenErrorMessage) {
    message = accessTokenErrorMessage;
  }

  // Render logic

  // Signing out — render nothing, and do so BEFORE the error screen. A later
  // DEAUTH_USER can set `logout` while `retrievedUserInfo` is still
  // 'health-check-transient-error', so checking logout first prevents the
  // retryable error UI (and its Try again → revalidate) from showing during an
  // in-flight native logout. Covers all states, not just SUCCESS.
  if (logout) {
    return null;
  }

  if (message) {
    return (
      <div className="splashScreen">
        <div className="logo-container">
          <MarleyIcon />
        </div>
        <ErrorMessage
          message={message}
          loginId={loginId}
          canRetry={canRetry}
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // Show loading overlay until authentication is complete
  const isLoading = loadingStatus !== SUCCESS;

  return isLoading ? (
    <Loading />
  ) : (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
};

export const RequiredAuth = () => {
  const { authId, inboxFilters } = useSelector((state: StateType) => ({
    authId: state?.auth?.user?._id,
    inboxFilters: state?.profile?.properties?.inboxFilters,
  }));

  const revalidator = useRevalidator();

  const isInboxFiltersAvailable = !isEmpty(inboxFilters);

  // Trigger early API calls with revalidate when authId & inbox filters are ready.
  // Without this, React Router runs the loader later, delaying data fetches.
  // On mobile we skip revalidate since the way web view works can cause infinite loop.
  // (Long-term fix: move authId retrieval to react-query so the loader can await it and eliminate this hook
  useEffect(() => {
    if (authId && isInboxFiltersAvailable && !isLoadedFromMobile()) {
      revalidator.revalidate();
    }
  }, [authId, isInboxFiltersAvailable, revalidator]);

  // Keep UserInit separate from RequiredAuth to avoid running auth-dependent hooks
  // before the JWT is available. We can’t use a loader since Okta’s authState starts
  // as null and updates asynchronously; with components we can return null to block
  // rendering, but loaders still render children. Awaiting authState isn’t possible
  // as Okta provides no API for it.

  const { authState } = useAuthenticationRedirect();
  if (!authState?.isAuthenticated) {
    return null;
  }
  return <UserInit />;
};
